import React, { useEffect, useMemo, useState } from "react";
import { Layout, Row, Spin, Col, FloatButton } from "antd";
import RGL, { WidthProvider, Layouts } from "react-grid-layout";

import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./dashboard.css";

import KPIWidget from "./components/KPIWidget";
import SalesChart from "./components/SalesChart";
import PieChart from "./components/PieChart";
import TransactionsTable from "./components/TransactionsTable";

import { initialLayout } from "./initialLayout";
import coreFunctions from "../utils/coreFunctions";
import { CloseCircleOutlined, EditOutlined } from "@ant-design/icons";
import apiRequest from "../utils/apiRequest";

import { SalesOverview } from "src/modules/sales/types/types";
import { VehicleOverview } from "src/modules/vehicle/types";
import { JobOverview } from "src/modules/jobs/types";

import AIIMG from "src/assets/img/ai.gif";

const { Content } = Layout;

const Dashboard = () => {
  const ResponsiveGridLayout = WidthProvider(RGL.Responsive);

  const [layouts, setLayouts] = useState<Layouts>(initialLayout);
  const [salesOverview, setSalesOverview] = useState<SalesOverview>({
    total_sales: 0,
    total_income: 0,
    day_by_sales: [],
  });
  const [vehicleOverview, setVehicleOverview] = useState<VehicleOverview>({
    total_vehicles: 0,
    type_wise_vehicle_total: [],
  });
  const [jobOverview, setJobOverview] = useState<JobOverview>({
    status_wise_job_total: [],
  });
  const [loading, setLoading] = useState(true);
  const [makeEditable, setMakeEditable] = useState<boolean>(false);
  const isMobileDevice = coreFunctions.isMobileDevice();

  const onLayoutChange = (currentLayout: any[], allLayouts: Layouts) => {
    localStorage.setItem("layout", JSON.stringify(allLayouts));
  };

  useEffect(() => {
    if (!loading) {
      const savedLayout = localStorage.getItem("layout");
      if (savedLayout) {
        const parsedLayout = JSON.parse(savedLayout);
        setLayouts(parsedLayout);
      }
    }
  }, [loading]);

  useEffect(() => {
    if (!makeEditable) {
      const savedLayout = localStorage.getItem("layout");
      if (savedLayout) {
        const parsedLayout = JSON.parse(savedLayout);
        setLayouts(parsedLayout);
      }
    }
  }, [makeEditable]);

  useEffect(() => {
    const loadOverview = async () => {
      const SALES_FORECAST = new apiRequest("sales/forecast", true);
      await SALES_FORECAST.send({}, (data: SalesOverview) => {
        setSalesOverview(data);
      });

      const VEHICLE_FORECAST = new apiRequest("vehicles/forecast", true);
      await VEHICLE_FORECAST.send({}, (data: VehicleOverview) => {
        setVehicleOverview(data);
      });

      const JOB_FORECAST = new apiRequest("job/forecast", true);
      await JOB_FORECAST.send({}, (data: JobOverview) => {
        setJobOverview(data);
      });

      setLoading(false);
    };

    loadOverview();
  }, []);

  const kpiWidgets = useMemo(() => {
    return [
      <div key="kpi1" className="glass_effect">
        <KPIWidget
          icon="DollarOutlined"
          icon_color="#3f8600"
          title="Total Sales"
          value={salesOverview.total_sales}
          prefix=" LKR"
        />
      </div>,
      <div key="kpi2" className="glass_effect">
        <KPIWidget
          icon="DollarOutlined"
          icon_color="#d48806"
          title="New Income"
          value={salesOverview.total_income}
          prefix=" LKR"
        />
      </div>
      // <div key="kpi3" className="glass_effect">
      //   <KPIWidget
      //     icon="CarOutlined"
      //     title="Total Vehicles"
      //     value={vehicleOverview.total_vehicles}
      //   />
      // </div>,
      // <div key="kpi4" className="glass_effect">
      //   <KPIWidget
      //     icon="ClockCircleOutlined"
      //     icon_color="#d48806"
      //     title="Pending Jobs"
      //     value={
      //       jobOverview.status_wise_job_total.find(
      //         (status) => status.slug === "pending"
      //       )?.value || 0
      //     }
      //   />
      // </div>,
      // <div key="kpi5" className="glass_effect">
      //   <KPIWidget
      //     icon_color="#3f8600"
      //     icon="CheckCircleOutlined"
      //     title="Completed Jobs"
      //     value={
      //       jobOverview.status_wise_job_total.find(
      //         (status) => status.slug === "completed"
      //       )?.value || 0
      //     }
      //   />
      // </div>,
      // <div key="kpi6" className="glass_effect">
      //   <KPIWidget
      //     icon_color="#2510e0"
      //     icon="ClockCircleOutlined"
      //     title="In Service Jobs"
      //     value={
      //       jobOverview.status_wise_job_total.find(
      //         (status) => status.slug === "in_service"
      //       )?.value || 0
      //     }
      //   />
      // </div>,
      // <div key="kpi7" className="glass_effect">
      //   <KPIWidget
      //     icon_color="#3f8600"
      //     icon="CheckCircleOutlined"
      //     title="Delivered Jobs"
      //     value={
      //       jobOverview.status_wise_job_total.find(
      //         (status) => status.slug === "delivered"
      //       )?.value || 0
      //     }
      //   />
      // </div>,
      // <div key="kpi8" className="glass_effect">
      //   <KPIWidget
      //     icon_color="#c91a34"
      //     icon="CloseCircleOutlined"
      //     title="Cancelled Jobs"
      //     value={
      //       jobOverview.status_wise_job_total.find(
      //         (status) => status.slug === "cancelled"
      //       )?.value || 0
      //     }
      //   />
      // </div>,
    ];
  }, [salesOverview, vehicleOverview, jobOverview]);

  const actionButtons = useMemo(() => {
    if (isMobileDevice) return null;
    return (
      <FloatButton.Group
        shape="circle"
        type="primary"
        style={{ insetInlineEnd: 24 }}
      >
        <FloatButton
          tooltip={
            <span>{makeEditable ? "Disable Editing" : "Enable Editing"}</span>
          }
          onClick={() => setMakeEditable(!makeEditable)}
          icon={makeEditable ? <CloseCircleOutlined /> : <EditOutlined />}
        />
      </FloatButton.Group>
    );
  }, [isMobileDevice, makeEditable]);

  return loading ? (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "linear-gradient(135deg, #f0f2f5, #e0e0e0)",
        }}
      >
        <img
          src={AIIMG}
          alt="AI"
          style={{
            width: "80px",
            marginBottom: "24px",
            filter: "drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15))",
          }}
        />
        <p
          style={{
            color: "#333",
            textAlign: "center",
            marginBottom: "8px",
            opacity: 0.5,
          }}
        >
          Preparing your intelligent dashboard <br /> <small>[BETA]</small>
        </p>
      </div>
    </>
  ) : (
    <Layout style={{ minHeight: "100vh", width: "100%" }}>
      <Content style={{ width: "100%" }}>
        <ResponsiveGridLayout
          className="layout"
          layouts={layouts}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 12, md: 10, sm: 8, xs: 4, xxs: 1 }}
          rowHeight={100}
          isDraggable={!isMobileDevice && makeEditable}
          isResizable={makeEditable}
          onLayoutChange={onLayoutChange}
        >
          {kpiWidgets}
          {!isMobileDevice && (
            <div key="chart1" className="glass_effect">
              <SalesChart overview={salesOverview} loading={loading} />
            </div>
          )}
          {/* {!isMobileDevice && (
            <div key="chart2" className="glass_effect">
              <PieChart vehicleOverview={vehicleOverview} loading={loading} />
            </div>
          )} */}
          {!isMobileDevice && (
            <div key="table1">
              <TransactionsTable />
            </div>
          )}
        </ResponsiveGridLayout>

        {!isMobileDevice && actionButtons}
      </Content>
    </Layout>
  );
};

export default Dashboard;
